import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Video, Upload, X, Scissors } from "lucide-react";

const PROGRAMS = ["PhD", "EdD", "DBA", "DNP", "PsyD", "DrPH", "Masters"];
const STAGES = [
  { v: "coursework", l: "Coursework" }, { v: "comps", l: "Comprehensive Exams" },
  { v: "proposal", l: "Proposal" }, { v: "irb", l: "IRB" },
  { v: "research", l: "Research" }, { v: "analysis", l: "Analysis" },
  { v: "dissertation", l: "Dissertation Writing" }, { v: "defense", l: "Defense" },
  { v: "graduation", l: "Graduation" }, { v: "publishing", l: "Publishing" },
];

const MAX_TRIM = 60;

function fmt(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", program: user?.program || "PhD",
    stage: user?.stage || "coursework", field_of_study: user?.field_of_study || "",
    methodology: user?.methodology || "", institution: user?.institution || "",
    bio: user?.bio || "", chair_email: user?.chair_email || "",
    committee_emails_text: (user?.committee_emails || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [pickedUrl, setPickedUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const blobUrlRef = useRef(null);
  const pickedUrlRef = useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const canMentor = ["mentor", "coach", "graduate", "faculty"].includes(user?.role);
  const hasVideo = !!user?.intro_video_path;

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      if (pickedUrlRef.current) URL.revokeObjectURL(pickedUrlRef.current);
    };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", {
        name: form.name, program: form.program, stage: form.stage,
        field_of_study: form.field_of_study, methodology: form.methodology,
        institution: form.institution, bio: form.bio,
        chair_email: form.chair_email,
        committee_emails: form.committee_emails_text.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refresh();
      toast.success("Profile updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) { toast.error("Video must be under 60MB"); return; }
    if (pickedUrlRef.current) URL.revokeObjectURL(pickedUrlRef.current);
    const url = URL.createObjectURL(file);
    pickedUrlRef.current = url;
    setPickedFile(file);
    setPickedUrl(url);
    setTrimStart(0);
    setTrimEnd(0);
    setDuration(0);
  };

  const onLoadedMeta = () => {
    const d = videoRef.current?.duration || 0;
    setDuration(d);
    setTrimEnd(Math.min(d, MAX_TRIM));
  };

  const upload = async () => {
    if (!pickedFile) return;
    const trimming = duration > 0 && (trimStart > 0 || trimEnd < duration);
    if (trimming) {
      if (trimEnd - trimStart < 1) { toast.error("Trim range must be at least 1 second"); return; }
      if (trimEnd - trimStart > MAX_TRIM) { toast.error(`Trim must be ${MAX_TRIM}s or less`); return; }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      if (trimming) {
        fd.append("trim_start", String(trimStart));
        fd.append("trim_end", String(trimEnd));
      }
      const r = await api.post("/mentors/intro-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.trimmed ? "Trimmed & uploaded." : "Uploaded.");
      setPickedFile(null); setPickedUrl(null); setDuration(0);
      if (pickedUrlRef.current) { URL.revokeObjectURL(pickedUrlRef.current); pickedUrlRef.current = null; }
      await refresh();
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const loadVideo = async () => {
    try {
      const r = await fetch(`${API}/mentors/${user.id}/intro-video`, { credentials: "include" });
      if (!r.ok) throw new Error("no video");
      const b = await r.blob();
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(b);
      blobUrlRef.current = url;
      setVideoBlob(url);
    } catch { toast.error("Could not load video"); }
  };

  const deleteVideo = async () => {
    try {
      await api.delete("/mentors/intro-video");
      setVideoBlob(null);
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      await refresh();
      toast.success("Video removed.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelPick = () => {
    if (pickedUrlRef.current) { URL.revokeObjectURL(pickedUrlRef.current); pickedUrlRef.current = null; }
    setPickedFile(null); setPickedUrl(null); setDuration(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const seekPreview = (t) => {
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.pause();
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl" data-testid="profile-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Profile</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">Your story so far.</h1>

      <Card className="mt-10 p-6 lg:p-8 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" />
          </div>
          <div>
            <Label>Program</Label>
            <Select value={form.program} onValueChange={(v) => set("program", v)}>
              <SelectTrigger data-testid="profile-program"><SelectValue /></SelectTrigger>
              <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stage</Label>
            <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
              <SelectTrigger data-testid="profile-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Field of study</Label>
            <Input value={form.field_of_study} onChange={(e) => set("field_of_study", e.target.value)} data-testid="profile-field" />
          </div>
          <div>
            <Label>Methodology</Label>
            <Input value={form.methodology} onChange={(e) => set("methodology", e.target.value)} data-testid="profile-methodology" />
          </div>
          <div className="col-span-2">
            <Label>Institution</Label>
            <Input value={form.institution} onChange={(e) => set("institution", e.target.value)} data-testid="profile-institution" />
          </div>
          <div className="col-span-2">
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} className="min-h-[100px]" data-testid="profile-bio" />
          </div>

          <div className="col-span-2 border-t border-border pt-4 mt-2">
            <p className="text-xs uppercase tracking-[0.25em] text-primary mb-1">Committee</p>
            <p className="text-xs text-muted-foreground">These addresses stay private. Loop in your chair (or the whole committee) from any circle.</p>
          </div>
          <div>
            <Label>Chair email</Label>
            <Input type="email" value={form.chair_email} onChange={(e) => set("chair_email", e.target.value)} data-testid="profile-chair-email" placeholder="chair@university.edu" />
          </div>
          <div>
            <Label>Committee members (comma-separated)</Label>
            <Input value={form.committee_emails_text} onChange={(e) => set("committee_emails_text", e.target.value)} data-testid="profile-committee" placeholder="dr.smith@…, dr.jones@…" />
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="profile-save-btn">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </Card>

      {canMentor && (
        <Card className="mt-8 p-6 lg:p-8" data-testid="intro-video-card">
          <div className="flex items-center gap-2 mb-2">
            <Video className="h-5 w-5 text-primary" />
            <h2 className="font-serif text-2xl font-black">Intro video</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Upload a short introduction. Trim to under 60 seconds using the sliders before publishing.
          </p>

          {pickedUrl ? (
            <div className="mb-4 space-y-4">
              <video
                ref={videoRef}
                src={pickedUrl}
                controls
                onLoadedMetadata={onLoadedMeta}
                className="w-full rounded-lg bg-black max-h-[300px]"
                data-testid="trim-preview-video"
              />
              {duration > 0 && (
                <div className="space-y-4" data-testid="trim-controls">
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <Scissors className="h-3 w-3" />
                    <span className="uppercase tracking-widest">Trim ({fmt(trimEnd - trimStart)} · max {MAX_TRIM}s)</span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>Start</span><span className="text-muted-foreground">{fmt(trimStart)}</span>
                    </div>
                    <input
                      type="range" min={0} max={duration} step={0.1}
                      value={trimStart}
                      onChange={(e) => {
                        const v = Math.min(Number(e.target.value), trimEnd - 0.5);
                        setTrimStart(v); seekPreview(v);
                      }}
                      className="w-full accent-primary"
                      data-testid="trim-start-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>End</span><span className="text-muted-foreground">{fmt(trimEnd)}</span>
                    </div>
                    <input
                      type="range" min={0} max={duration} step={0.1}
                      value={trimEnd}
                      onChange={(e) => {
                        const v = Math.max(Number(e.target.value), trimStart + 0.5);
                        setTrimEnd(Math.min(v, trimStart + MAX_TRIM));
                        seekPreview(Math.min(v, trimStart + MAX_TRIM));
                      }}
                      className="w-full accent-primary"
                      data-testid="trim-end-input"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Trimmed length: <span className="text-foreground font-medium">{fmt(trimEnd - trimStart)}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={upload} disabled={uploading} className="rounded-full" data-testid="upload-video-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "Processing…" : "Publish trimmed video"}
                </Button>
                <Button variant="outline" onClick={cancelPick} className="rounded-full" data-testid="cancel-pick-btn">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {videoBlob ? (
                <video src={videoBlob} controls className="w-full rounded-lg bg-black mb-4 max-h-[300px]" data-testid="profile-video-player" />
              ) : hasVideo ? (
                <Button variant="outline" className="mb-4 rounded-full" onClick={loadVideo} data-testid="preview-video-btn">
                  Preview current video
                </Button>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <input
                  ref={fileRef} type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                  className="hidden" data-testid="intro-video-input"
                />
                <Button onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="pick-video-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  {hasVideo ? "Replace video" : "Choose intro video"}
                </Button>
                {hasVideo && (
                  <Button variant="outline" onClick={deleteVideo} className="rounded-full" data-testid="delete-video-btn">
                    <X className="h-4 w-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
